import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  WelcomeEmailService,
  WelcomeEmailUserInput,
  WelcomeEmailPreview,
} from '../../../@services/welcome-email.service';
import { ToastService } from '../../../@services/toast.service';

// Users → Actions → Send Welcome Email modal. Opens with a set of
// users, calls /rest/welcome-email/preview to render an A/B sample
// using the first user's data, and lets the operator send after
// reviewing. Bypasses per-customer commerce_settings.welcome_email_
// enabled — admin explicitly clicking Send IS the intent.
type TabKey = 'a' | 'b' | 'skipped';

@Component({
  standalone: true,
  selector: 'app-welcome-email-modal',
  imports: [CommonModule],
  templateUrl: './welcome-email-modal.component.html',
})
export class WelcomeEmailModalComponent implements OnChanges {
  @Input() show = false;
  @Input() users: WelcomeEmailUserInput[] = [];
  @Output() closed = new EventEmitter<void>();
  @Output() sent = new EventEmitter<{ sent: number; failed: number; skipped: number }>();
  // IT-F2-<pending>: parent handles the actual mint + clipboard write
  // (already owns the mintMagicLink → copyText plumbing from the
  // Override Password flow). Modal just emits the request keyed by
  // email so the parent can look up username + pool_id from its full
  // grid state.
  @Output() copyMagicLinkFor = new EventEmitter<{ email: string; customer_slug: string }>();

  // Per-row busy set so double-clicks on the same row don't re-fire
  // the mint. Keyed by email — same shape the preview groups use.
  copyingFor = new Set<string>();

  loading = false;
  sending = false;
  preview: WelcomeEmailPreview | null = null;
  activeTab: TabKey = 'a';

  private readonly svc = inject(WelcomeEmailService);
  private readonly toast = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnChanges(changes: SimpleChanges): void {
    // Only fetch preview on transition from hidden → shown, so users
    // can toggle tabs / re-render without extra API calls.
    if (changes['show']) {
      const prev = changes['show'].previousValue;
      const now = changes['show'].currentValue;
      if (!prev && now) {
        this.activeTab = 'a';
        this.preview = null;
        this._load_preview();
      }
    }
  }

  private async _load_preview() {
    if (!this.users || this.users.length === 0) {
      this.toast.warning('No users', 'Select at least one user first.');
      this.close();
      return;
    }
    this.loading = true;
    try {
      const res = await this.svc.preview(this.users);
      this.preview = res;
      // If the initial tab has no rows, jump to the first non-empty one
      // so the operator lands on something useful.
      if (res) {
        if (res.a.count > 0) this.activeTab = 'a';
        else if (res.b.count > 0) this.activeTab = 'b';
        else if (res.skipped.count > 0) this.activeTab = 'skipped';
      }
    } finally {
      this.loading = false;
    }
  }

  setTab(t: TabKey) { this.activeTab = t; }

  // srcdoc-friendly HTML — trust it because it came from OUR backend
  // that we control. The <iframe> is sandboxed in the template so a
  // template escape mistake still can't hijack the admin session.
  safeHtmlFor(html?: string): SafeHtml | null {
    if (!html) return null;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // Deliverable count = A + B (skipped won't be sent). Used on the
  // Send button label + confirm dialog so the operator sees the exact
  // outcome before committing.
  get deliverableCount(): number {
    return (this.preview?.a?.count || 0) + (this.preview?.b?.count || 0);
  }

  async onSend() {
    if (!this.preview || this.deliverableCount === 0) return;
    const a = this.preview.a.count;
    const b = this.preview.b.count;
    const skipped = this.preview.skipped.count;
    const parts: string[] = [];
    if (a) parts.push(`${a} magic-link`);
    if (b) parts.push(`${b} branded-activation`);
    const skipNote = skipped ? ` (${skipped} will be skipped)` : '';
    if (!confirm(`Send ${this.deliverableCount} welcome email${this.deliverableCount === 1 ? '' : 's'}: ${parts.join(' + ')}${skipNote}?`)) return;
    this.sending = true;
    try {
      const res = await this.svc.send(this.users);
      if (!res) return;
      this.toast.success('Welcome emails sent',
        `${res.sent} sent, ${res.skipped} skipped, ${res.failed} failed`);
      this.sent.emit({ sent: res.sent, failed: res.failed, skipped: res.skipped });
      this.close();
    } finally {
      this.sending = false;
    }
  }

  close() {
    this.closed.emit();
  }

  /** Emit the mint-and-copy request to the parent, which owns the
   *  actual mintMagicLink() + copyText() flow (already used by
   *  Override Password → Copy magic-link URL). Parent will emit a
   *  toast on success/failure — modal just spins the row briefly so
   *  a rapid double-click can't fire the mint endpoint twice.
   *
   *  Parent should call resetCopyingFor(email) after its handler
   *  completes to clear the spinner; we auto-clear on a 3s fallback
   *  in case the parent forgets or errors before signaling. */
  onCopyMagicLink(u: { email: string; customer_slug: string }) {
    if (this.copyingFor.has(u.email)) return;
    this.copyingFor.add(u.email);
    this.copyMagicLinkFor.emit({ email: u.email, customer_slug: u.customer_slug });
    setTimeout(() => this.copyingFor.delete(u.email), 3000);
  }

  isCopying(email: string): boolean { return this.copyingFor.has(email); }
}
